/* eslint-disable no-console -- logs errors to console for developers to backtrace errors*/
import type { NavOptions, ProductCategory, ProductUrls } from './types';
import {
  createCategory,
  setCssVariables,
  updateDisabledProducts,
  updateFeatureFlags,
  updateProductUrls,
} from './utils';

import './style.css';

const optionDefaults: NavOptions = {
  anchorId: null,
  featureFlags: {
    showSurvey: true,
    showDigital: true,
    showReputation: true,
    showWorkflow: true,
    showDashboards: true,
    showPulse: true,
    showListings: true,
  },
  productUrls: {
    survey: 'https://app.alchemer.com/',
    digital: 'https://app.alchemer.com/index/digital',
    reputation: 'https://live.chatmeter.com/#/reviews/summary',
    workflow: 'https://app.alchemer.com/index/workflows',
    dashboard: 'https://clarity.alchemer.com/',
    pulse: 'https://app.alchemer.com/index/pulse',
    listings: 'https://live.chatmeter.com/#/listings/accuracy/managed',
  },
  cssVariables: {
    toggleAnimationDuration: '0.2s',
    primaryBgColor: 'var(--alc-white)',
    categoryColor: 'var(--alc-accent-foreground)',
    productColor: 'var(--alc-accent-foreground)',
    productIconColor: 'var(--alc-accent-foreground)',
    productSelectedColor: 'var(--alc-aqua-800)',
    productSelectedBgColor: 'var(--alc-aqua-200)',
    productMouseOverColor: 'var(--alc-accent-foreground)',
    productMouseOverBgColor: 'var(--alc-accent)',
    productActiveColor: 'var(--alc-accent-foreground)',
    productActiveBgColor: 'var(--alc-accent)',
    newProductBadgeColor: 'var(--alc-sky-500)',
    newProductBadgeBgColor: 'var(--alc-sky-100)',
  },
};

const menuItems: ProductCategory[] = [
  {
    category: 'Collect data',
    items: [
      { label: 'survey', icon: 'alchemerSurvey' },
      { label: 'digital', icon: 'alchemerDigital' },
      { label: 'reputation', icon: 'lucideStar' },
    ],
  },
  {
    category: 'Connect systems',
    items: [{ label: 'workflow', icon: 'alchemerWorkflow' }],
  },
  {
    category: 'Clarify results',
    items: [
      { label: 'dashboard', icon: 'lucideLayoutDashboard' },
      { label: 'pulse', icon: 'alchemerPulse' },
    ],
  },
  {
    category: 'Compass',
    items: [{ label: 'listings', icon: 'lucideMapPin' }],
  },
];

/**
 * Creates a universal navigation dropdown menu with the provided custom options.
 * Prevents multiple navigation menus from being created.
 */
const createUniNav = (customOptions: NavOptions): void => {
  if (document.getElementById('uniNavContainer')) {
    return;
  }

  const options = { ...optionDefaults, ...customOptions };

  if (!options.anchorId) {
    console.error('Anchor ID is required');
    return;
  }

  const anchor = document.getElementById(options.anchorId);
  if (!anchor) {
    console.error('Anchor not found:', options.anchorId);
    return;
  }

  if (options.cssVariables) {
    setCssVariables(options.cssVariables);
  }

  if (!options.productUrls) {
    console.error('Product URLs are required');
    return;
  }
  const menuCategories = updateFeatureFlags(
    updateDisabledProducts(updateProductUrls(options.productUrls, menuItems), options.disabledProducts ?? []),
    options.featureFlags,
  );

  anchor.style.position = 'relative';

  const categoriesHTML = menuCategories.map((category) => createCategory(category, options.selectedMenuItem)).join('');

  const itemsHTML = `
  <div class="uni-nav__container">
    ${categoriesHTML}
  </div>
`;

  const navContainer = document.createElement('div');
  navContainer.id = 'uniNavContainer';
  navContainer.classList.add(
    'uni-nav-theme',
    'uni-nav__nav-container-init',
    'uni-nav__nav-container',
    'uni-nav__nav-container--hide',
  );
  navContainer.innerHTML = itemsHTML;
  anchor.appendChild(navContainer);

  navContainer.querySelectorAll<HTMLElement>('.uni-nav__category-item-wrapper--disabled').forEach((wrapper) => {
    wrapper.addEventListener('click', (e) => {
      const infoBtn = (e.target as HTMLElement).closest('.uni-nav__category-item-info-btn');
      if (!infoBtn) {
        e.stopPropagation();
      }
    });
  });

  let blurCleanup: (() => void) | null = null;

  const openMenu = (): void => {
    navContainer.classList.remove('uni-nav__nav-container-init', 'uni-nav__nav-container--hide');
    navContainer.classList.add('uni-nav__nav-container--show');
    anchor.classList.add('uni-nav__trigger--open');

    const handleBlur = () => {
      if (document.activeElement?.tagName === 'IFRAME') {
        closeMenu();
      }
    };
    blurCleanup?.();
    window.addEventListener('blur', handleBlur);
    blurCleanup = () => window.removeEventListener('blur', handleBlur);
  };

  const closeMenu = (): void => {
    navContainer.classList.remove('uni-nav__nav-container-init', 'uni-nav__nav-container--show');
    navContainer.classList.add('uni-nav__nav-container--hide');
    anchor.classList.remove('uni-nav__trigger--open');

    blurCleanup?.();
    blurCleanup = null;
  };

  const isOpen = () => navContainer.classList.contains('uni-nav__nav-container--show');

  anchor.addEventListener('click', (e: Event) => {
    e.stopPropagation();
    if (isOpen()) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  document.addEventListener('click', (e: MouseEvent) => {
    e.stopPropagation();
    const htmlElementClicked = e.target as HTMLElement;
    if (!anchor.contains(htmlElementClicked)) {
      closeMenu();
    }
  });
};

/**
 * Updates the product URLs in an existing universal navigation menu
 */
export const updateUniNavUrls = (productUrls: Partial<ProductUrls>): void => {
  const navContainer = document.getElementById('uniNavContainer');
  if (!navContainer) {
    console.warn('[UniNav] Cannot update URLs: navigation not initialized');
    return;
  }

  Object.entries(productUrls).forEach(([product, url]) => {
    const links = navContainer.querySelectorAll<HTMLAnchorElement>(`a[data-product="${product}"]`);
    links.forEach((link) => {
      link.href = url;
    });
  });
};

export default createUniNav;
